import { LEVEL } from '../../../../constant'
import { PROFILE_GENDER, PROFILE_KEY, PROFILE_TRAINING_FREQUENCY } from '../../constants'
import { PROFILE_BMI_CATEGORY_RANGES, PROFILE_BMI_CATEGORY_TO_BODY_TYPE } from '../../constants/bmi'

import { getMetersHeightUtil } from '../../../../modules/height/utils/get-meters'
import { getPathReduxUtil } from '../../../../modules/redux/utils/get-path'
import { getValueReduxUtil } from '../../../../modules/redux/utils/get-value'
import updateReduxUtil from '../../../../modules/redux/utils/update'
import { compactArrayUtil, differenceInYearsDateUtil } from 'packages/src'
import setUtil from '../../../../utils/set'
import { getKgsWeightUtil } from '../../../../modules/weight/utils/get-kgs'

const BMI_FITNESS_DATA_KEYS = [
  PROFILE_KEY.TRAINING_FREQUENCY,
  PROFILE_KEY.GENDER,
  PROFILE_KEY.BIRTHDAY,
  PROFILE_KEY.WEIGHT,
  PROFILE_KEY.WEIGHT_UNIT,
  PROFILE_KEY.HEIGHT,
  PROFILE_KEY.HEIGHT_UNIT
]

// Estimate PAL (Physical Activity Level)
// @see {@link
// https://en.wikipedia.org/wiki/Harris–Benedict_equation#Step_2_–_Determine_Total_Intake}
const TRAINING_FREQUENCY_TO_PHYSICAL_ACTIVITY_LEVEL = {
  [PROFILE_TRAINING_FREQUENCY.NOT_ACTIVE]: 1.5,
  [PROFILE_TRAINING_FREQUENCY.OCCASIONALLY]: 1.6,
  [PROFILE_TRAINING_FREQUENCY.FREQUENTLY]: 1.8,
  [PROFILE_TRAINING_FREQUENCY.VERY_FREQUENTLY]: 2
}

const FITNESS_LEVEL_TO_TRAINING_FREQUENCY = {
  [LEVEL.BEGINNER]: PROFILE_TRAINING_FREQUENCY.OCCASIONALLY,
  [LEVEL.INTERMEDIATE]: PROFILE_TRAINING_FREQUENCY.FREQUENTLY,
  [LEVEL.ADVANCED]: PROFILE_TRAINING_FREQUENCY.VERY_FREQUENTLY,
  [LEVEL.EXPERT]: PROFILE_TRAINING_FREQUENCY.VERY_FREQUENTLY
}

const TRAINING_FREQUENCY_TO_WEEKLY_WORKOUTS_GOAL = {
  [PROFILE_TRAINING_FREQUENCY.NOT_ACTIVE]: 1,
  [PROFILE_TRAINING_FREQUENCY.OCCASIONALLY]: 1,
  [PROFILE_TRAINING_FREQUENCY.FREQUENTLY]: 4,
  [PROFILE_TRAINING_FREQUENCY.VERY_FREQUENTLY]: 5
}

function updateProfileInState (profile, updates, state) {
  if (!Object.keys(updates).length) {
    return state
  }
  const newInstance = updateReduxUtil(profile, updates)
  return setUtil(state, getPathReduxUtil(newInstance), newInstance)
}

function updateProfileFitnessData (profile, state) {
  const updates = {}

  // Set the default fitness level
  const level = getValueReduxUtil(profile, PROFILE_KEY.FITNESS_LEVEL)
  const goal = getValueReduxUtil(profile, PROFILE_KEY.GOAL)
  if (goal && !level) {
    updates[PROFILE_KEY.FITNESS_LEVEL] = (
      LEVEL.BEGINNER
    )
  }

  // If the training level is provided, estimate training frequency
  const trainingFrequency = getValueReduxUtil(profile,
    PROFILE_KEY.TRAINING_FREQUENCY
  )
  if (level && !trainingFrequency) {
    updates[PROFILE_KEY.TRAINING_FREQUENCY] = (
      FITNESS_LEVEL_TO_TRAINING_FREQUENCY[level]
    )
  }

  // If the training frequency is provided, estimate weekly workouts count goal
  const weeklyWorkoutsCountGoal = getValueReduxUtil(profile,
    PROFILE_KEY.WEEKLY_WORKOUTS_COUNT_GOAL
  )
  if (trainingFrequency && !weeklyWorkoutsCountGoal) {
    updates[PROFILE_KEY.WEEKLY_WORKOUTS_COUNT_GOAL] = (
      TRAINING_FREQUENCY_TO_WEEKLY_WORKOUTS_GOAL[level]
    )
  }

  // If no key has changed, no-op
  // const changedFitnessData = pickObjectUtil(profile.changed, BMI_FITNESS_DATA_KEYS)
  // if (
  //   !Object.keys(changedFitnessData).length &&
  //   getValueReduxUtil(
  //     profile,
  //     PROFILE_KEY.TOTAL_ENERGY_EXPENDITURE
  //   )
  // ) {
  //   return updateProfileInState(profile, updates, state)
  // }

  // If a not all the keys are available, no-op
  const fitnessDataArr = compactArrayUtil(
    BMI_FITNESS_DATA_KEYS.map(key => getValueReduxUtil(profile, key))
  )

  if (fitnessDataArr.length < BMI_FITNESS_DATA_KEYS.length) {
    return updateProfileInState(profile, updates, state)
  }

  // Calculate BMI (Body Mass Index)
  const heightVal = parseFloat(getValueReduxUtil(profile, PROFILE_KEY.HEIGHT))
  if (Number.isNaN(heightVal)) {
    return updateProfileInState(profile, updates, state)
  }
  const height = getMetersHeightUtil(heightVal,
    getValueReduxUtil(profile, PROFILE_KEY.HEIGHT_UNIT)
  )

  const heightUnit = getValueReduxUtil(profile, PROFILE_KEY.HEIGHT_UNIT)
  const weightVal = parseFloat(getValueReduxUtil(profile, PROFILE_KEY.WEIGHT))
  if (Number.isNaN(weightVal)) {
    return updateProfileInState(profile, updates, state)
  }

  // If the height unit changes, assume the distance unit also matches
  // e.g. feet => miles
  if (!getValueReduxUtil(profile, PROFILE_KEY.DISTANCE_UNIT)) {
    updates[PROFILE_KEY.DISTANCE_UNIT] = heightUnit
  }

  const weightUnit = getValueReduxUtil(profile, PROFILE_KEY.WEIGHT_UNIT)
  const weight = getKgsWeightUtil(weightVal, weightUnit)
  const bmi = Math.round(weight / Math.pow(height, 2))
  const bmiCategory = Object
    .keys(PROFILE_BMI_CATEGORY_RANGES)
    .find(key => {
      const range = PROFILE_BMI_CATEGORY_RANGES[key]
      const [min, max] = range
      return bmi >= min && bmi <= max
    })
  updates[PROFILE_KEY.BMI] = bmi
  updates[PROFILE_KEY.BMI_CATEGORY] = (
    bmiCategory
      ? parseInt(bmiCategory, 10)
      : null
  )

  // Suggest body type
  if (bmiCategory && !getValueReduxUtil(profile, PROFILE_KEY.BODY_TYPE)) {
    updates[PROFILE_KEY.BODY_TYPE] = PROFILE_BMI_CATEGORY_TO_BODY_TYPE[bmiCategory]
  }

  // Calculate BMR (Basal Metabolic Rate)
  // @see {@link https://en.wikipedia.org/wiki/Harris%E2%80%93Benedict_equation
  let bmr
  const heightInCms = height * 100
  const gender = getValueReduxUtil(profile, PROFILE_KEY.GENDER)
  const birthday = getValueReduxUtil(profile, PROFILE_KEY.BIRTHDAY)
  const age = differenceInYearsDateUtil(new Date(), new Date(birthday), 'years')
  if (gender === PROFILE_GENDER.MALE) {
    bmr = Math.round((
      10 * weight
    ) + (
      6.25 * heightInCms
    ) - (
      5 * age
    ) + 5)
  } else {
    bmr = Math.round((
      10 * weight
    ) + (
      6.25 * heightInCms
    ) - (
      5 * age
    ) - 161)
  }
  updates[PROFILE_KEY.BMR] = bmr

  // Calculate TDEE (Total daily energy expenditure)
  // @see {@link
  // https://en.wikipedia.org/wiki/Harris–Benedict_equation#Step_2_–_Determine_Total_Intake}
  const physicalActivityLevel = TRAINING_FREQUENCY_TO_PHYSICAL_ACTIVITY_LEVEL[trainingFrequency]
  let totalEnergyExpenditure
  if (physicalActivityLevel) {
    totalEnergyExpenditure = Math.round(bmr * physicalActivityLevel)
  }
  updates[PROFILE_KEY.TOTAL_ENERGY_EXPENDITURE] = totalEnergyExpenditure

  return updateProfileInState(profile, updates, state)
}

function afterUpdateProfileHook (profile, state) {
  return updateProfileFitnessData(profile, state)
}

export {
  afterUpdateProfileHook
}
